import React, { useState, useEffect, useMemo } from "react";
import DashboardMenu from "../../commonComponents/DashboardMenu";
import DataTable from "react-data-table-component";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import useStyles from "../DashboardAuction/dashAuction.style";
import sellerListAPI from "../../../api/adminAPI/sellerListAPI";
import AuctionView from "../DashboardAuction/AuctionView";
import BidlogFirst from "../DashboardAuction/BidlogFirst";
import BidlogSecond from "../DashboardAuction/BidogSecond";
import DashBuyerDetail from "../DashboardAuction/BidlogFirst/DashBuyerDetail";
import DashEventDetail from "../DashboardAuction/BidlogFirst/DashEventDetail";
import BuyerBidLog from "../DashboardAuction/BidlogFirst/BuyerBidLog";
import EditEvent from "../DashboardAuction/EditDashEvent/EditEvent";
import AuctionTable from "../DashboardAuction/AuctionTable";
import EditTabEvent from "../DashboardAuction/EditDashEvent/EditTabEvent";
import AuctionViewFirst from "../DashboardAuction/BidlogFirst/AuctionViewFirst";
import AuctionSummary from "../DashboardAuction/BidlogFirst/AuctionSummary";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import sellerDeleteAPI from "../../../api/adminAPI/sellerDeleteAPI";
import sellerStatusUpdateAPI from "../../../api/adminAPI/sellerUpdateStatusAPI";
import sellerUpdateRightsAPI from "../../../api/adminAPI/sellerUpdateRightsAPI";
import getCountries from "../../../api/getContriesAPI";
import getStateAPI from "../../../api/getStateAPI";
import { isEmpty, omit } from "lodash";
import { getUserData, openAccessAccountTab } from "../../../common";
import getSellerDetails from "../../../api/adminAPI/getSellerDetails";
import updateSellerAPI from "../../../api/adminAPI/updateSellerAPI";
import { Navigate } from "react-router-dom";
import moment from "moment";

import { FaRegFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import SellerProfileModal from "./SellerProfileModal";
import accessSellerAccountAPI from "../../../api/adminAPI/accessSellerAccountAPI";
import LoginIcon from "@mui/icons-material/Login";

// organisers are users with the SELLER role; "Organiser" is the word shown on
// screen

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "");

// days counted by the automatic rule: since the last auction was created,
// joining, or an admin giving rights back / reactivating - whichever is latest
const daysIdle = (seller) =>
  seller.idleSince ? moment().diff(moment(seller.idleSince), "days") : null;

// the automatic rule (backend organiser-inactivity.service.js); the numbers
// mirror organiserInactivityConfig
const AUTO_RULE_TEXT =
  "Automatic rule: an organiser who creates no new auction for 30 days can no longer create auctions, and after 60 days is deactivated. Organisers with a live or upcoming auction are skipped. Allowing auction creation or activating them again restarts their count.";

const AUTO_ACTION_LABELS = {
  WITHDRAW_RIGHTS: "Stops auction creation",
  DEACTIVATE: "Deactivates",
};

const describeNextAutoAction = (next) => {
  if (!next) return "";
  if (next.paused) return "Paused: live or upcoming auction";
  const when = next.inDays === 0 ? "tonight" : `in ${next.inDays} days`;
  return `${AUTO_ACTION_LABELS[next.action]} ${when}`;
};

// what the confirm dialog says for each action, and the API that does it
const ACTIONS = {
  ACTIVATE: {
    title: (name) => `Activate ${name}?`,
    body: "They will be able to log in again.",
    confirm: "Activate",
    done: "Activated",
    callApi: (userId) => sellerStatusUpdateAPI({ status: "ACTIVE" }, userId),
  },
  DEACTIVATE: {
    title: (name) => `Deactivate ${name}?`,
    body: "They will not be able to log in. Their past auctions stay. Not possible while they have a live or upcoming auction.",
    confirm: "Deactivate",
    done: "Deactivated",
    callApi: (userId) => sellerStatusUpdateAPI({ status: "INACTIVE" }, userId),
  },
  GRANT_RIGHTS: {
    title: (name) => `Allow ${name} to create auctions?`,
    body: "They will be able to create new auctions.",
    confirm: "Allow",
    done: "Auction creation allowed for",
    callApi: (userId) => sellerUpdateRightsAPI({ auctionStatus: "YES" }, userId),
  },
  WITHDRAW_RIGHTS: {
    title: (name) => `Stop ${name} from creating auctions?`,
    body: "They will not be able to create new auctions. Auctions already created are not affected.",
    confirm: "Stop",
    done: "Auction creation stopped for",
    callApi: (userId) => sellerUpdateRightsAPI({ auctionStatus: "NO" }, userId),
  },
  DELETE: {
    title: (name) => `Delete ${name}?`,
    body: "They will not be able to log in and are removed from this list. Their past auctions stay. Not possible while they have a live or upcoming auction.",
    confirm: "Delete",
    done: "Deleted",
    callApi: (userId) => sellerDeleteAPI(userId),
  },
};

// columns of the Excel export, in order
const EXPORT_FIELDS = [
  ["Position", (seller) => seller.title],
  ["Organiser name", (seller) => seller.userName],
  ["Company", (seller) => seller.companyName],
  ["Mobile number", (seller) => seller.contactNumber],
  ["Alt. mobile number", (seller) => seller.alternateContactNumber || ""],
  ["Email", (seller) => seller.email],
  ["Business type", (seller) => seller.businessType],
  ["Joined on", (seller) => formatDate(seller.joinedAt)],
  ["Last auction created", (seller) => formatDate(seller.lastAuctionCreatedAt) || "Never"],
  ["Days idle", (seller) => daysIdle(seller) ?? ""],
  ["Next automatic step", (seller) => describeNextAutoAction(seller.nextAutoAction)],
  ["Auctions", (seller) => Number(seller.totalEvents)],
  ["Lots", (seller) => Number(seller.totalEventItems)],
  ["Bidders", (seller) => Number(seller.totalEventBuyers)],
  ["Participants", (seller) => Number(seller.totalParticipants)],
  ["Status", (seller) => seller.status],
  ["Can create auctions", (seller) => seller.auctionStatus],
];

const StatusChip = ({ status }) =>
  status === "ACTIVE" ? (
    <Chip size="small" color="success" label="Active" />
  ) : (
    <Chip size="small" label="Inactive" />
  );

const RightsChip = ({ auctionStatus }) =>
  auctionStatus === "NO" ? (
    <Chip size="small" variant="outlined" label="No" />
  ) : (
    <Chip size="small" color="primary" variant="outlined" label="Yes" />
  );

function DashSeller() {
  const classes = useStyles();
  const [changeView, SetChangeView] = useState(false);
  const [eventId, SetEventId] = useState("");
  const [buyerId, SetBuyerId] = useState("");
  const [buyerEventId, SetBuyerEventId] = useState("");
  const [buyerEventItemId, SetBuyerEvenItemtId] = useState("");
  const [eventItemId, SetEvenItemtId] = useState("");
  const [countriesList, updateCountriesList] = useState({
    apiState: "loading",
    list: [],
  });

  const [stateList, updateStateList] = useState({
    apiState: "loading",
    list: [],
  });

  const [tableSellers, setTableSellers] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sellerId = buyerId;

  // the ⋮ menu of one row: { anchorEl, seller }
  const [menu, setMenu] = useState(null);
  // the action waiting for confirmation: { action, seller }
  const [pending, setPending] = useState(null);
  const [filters, setFilters] = useState({ search: "", status: "", rights: "" });

  useEffect(() => {
    countryList();
    stateData();
  }, []);

  useEffect(() => {
    sellerList();
  }, [eventId]);

  useEffect(() => {
    if (sellerId) {
      getProfileData(sellerId);
    }
  }, [sellerId]);

  const stateData = async (countryId) => {
    try {
      const { data } = await getStateAPI(countryId);
      if (data) {
        updateStateList({ apiState: "success", list: data });
      } else {
        throw new Error(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const countryList = async () => {
    try {
      const { data } = await getCountries();
      if (data) {
        updateCountriesList({ apiState: "success", list: data });
      } else {
        throw new Error(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const sellerList = async () => {
    try {
      const { data } = await sellerListAPI();
      setTableSellers(
        (data || []).map((list) => ({
          companyName: list.companyName,
          businessType: list.businessType,
          title: list.title,
          userName: `${list.firstName} ${list.lastName}`,
          email: list.email,
          contactNumber: list.contactNumber,
          userId: list.userId,
          alternateContactNumber: list.alternateContactNumber,
          totalEventBuyers: list.totalEventBuyers,
          totalEventItems: list.totalEventItems,
          totalEvents: list.totalEvents,
          totalParticipants: list.totalParticipants,
          status: list.status,
          auctionStatus: list.auctionStatus,
          joinedAt: list.joinedAt,
          lastAuctionCreatedAt: list.lastAuctionCreatedAt,
          idleSince: list.idleSince,
          nextAutoAction: list.nextAutoAction,
        }))
      );
    } catch (error) {
      toast(error.response?.data?.error || "Failed to load organisers");
    }
    setListLoaded(true);
  };

  const filteredSellers = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return tableSellers.filter((seller) => {
      if (filters.status && seller.status !== filters.status) return false;
      if (filters.rights && (seller.auctionStatus || "YES") !== filters.rights) {
        return false;
      }
      if (!search) return true;
      return [
        seller.userName,
        seller.companyName,
        seller.email,
        seller.contactNumber,
        seller.alternateContactNumber,
      ].some((value) => String(value ?? "").toLowerCase().includes(search));
    });
  }, [tableSellers, filters]);

  const openMenu = (event, seller) => {
    SetBuyerId(seller.userId);
    setMenu({ anchorEl: event.currentTarget, seller });
  };

  const closeMenu = () => setMenu(null);

  const askToConfirm = (action, seller) => {
    closeMenu();
    setPending({ action, seller });
  };

  const runPendingAction = async () => {
    const { action, seller } = pending;
    setIsLoading(true);
    try {
      await ACTIONS[action].callApi(seller.userId);
      toast(`${ACTIONS[action].done} ${seller.userName}`);
      await sellerList();
    } catch (error) {
      toast(error.response?.data?.error || "Something went wrong");
    }
    setIsLoading(false);
    setPending(null);
  };

  const handleAccessSellerAccount = async (seller) => {
    closeMenu();
    try {
      const { data } = await accessSellerAccountAPI(seller.userId);
      const opened = openAccessAccountTab({
        token: data.token,
        userData: data.user,
      });

      if (!opened) {
        return toast("Please allow popups to open the support access tab");
      }

      toast(`Support access enabled for ${seller.userName}`);
    } catch (error) {
      toast(error.response?.data?.error || "Failed to access organiser account");
    }
  };

  const columns = [
    {
      name: "Organiser name",
      selector: (data) => data.userName,
      cell: (data) => (
        <div>
          <div>{data.userName}</div>
          {data.title && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>{data.title}</div>
          )}
        </div>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: "Company",
      selector: (data) => data.companyName,
      cell: (data) => String(data.companyName ?? ""),
      sortable: true,
      width: "150px",
    },
    {
      name: "Mobile number",
      selector: (data) => data.contactNumber,
      cell: (data) => (
        <div>
          <div>{data.contactNumber}</div>
          {data.alternateContactNumber && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Alt: {data.alternateContactNumber}
            </div>
          )}
        </div>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Email",
      selector: (data) => String(data.email ?? "").toLowerCase(),
      cell: (data) => String(data.email ?? ""),
      sortable: true,
      width: "200px",
    },
    {
      name: "Business type",
      selector: (data) => data.businessType,
      cell: (data) => String(data.businessType ?? ""),
      sortable: true,
      width: "140px",
    },
    {
      name: "Joined on",
      selector: (data) => data.joinedAt || "",
      cell: (data) => formatDate(data.joinedAt),
      sortable: true,
      width: "130px",
    },
    {
      name: "Last auction created",
      selector: (data) => data.lastAuctionCreatedAt || "",
      cell: (data) => formatDate(data.lastAuctionCreatedAt) || "Never",
      sortable: true,
      width: "180px",
    },
    {
      name: (
        <Tooltip title="Days since the last auction was created, joining, or an admin allowing auction creation / activating - whichever is latest">
          <span>Days idle</span>
        </Tooltip>
      ),
      selector: (data) => daysIdle(data) ?? -1,
      cell: (data) => String(daysIdle(data) ?? ""),
      sortable: true,
      width: "110px",
    },
    {
      name: "Next automatic step",
      selector: (data) =>
        data.nextAutoAction && !data.nextAutoAction.paused
          ? data.nextAutoAction.inDays
          : Number.MAX_SAFE_INTEGER,
      cell: (data) => {
        const next = data.nextAutoAction;
        const soon = next && !next.paused && next.inDays <= 7;
        return (
          <span style={{ color: soon ? "#b91c1c" : "#374151", fontSize: "13px" }}>
            {describeNextAutoAction(next)}
          </span>
        );
      },
      sortable: true,
      width: "210px",
    },
    {
      name: "Auctions",
      selector: (data) => parseInt(data.totalEvents, 10),
      cell: (data) => String(data.totalEvents),
      sortable: true,
      width: "110px",
    },
    {
      name: "Lots",
      selector: (data) => parseInt(data.totalEventItems, 10),
      cell: (data) => String(data.totalEventItems),
      sortable: true,
      width: "90px",
    },
    {
      name: "Bidders",
      selector: (data) => parseInt(data.totalEventBuyers, 10),
      cell: (data) => String(data.totalEventBuyers),
      sortable: true,
      width: "100px",
    },
    {
      name: "Participants",
      selector: (data) => parseInt(data.totalParticipants, 10),
      cell: (data) => String(data.totalParticipants),
      sortable: true,
      width: "130px",
    },
    {
      name: "Status",
      selector: (data) => data.status,
      cell: (data) => <StatusChip status={data.status} />,
      sortable: true,
      width: "110px",
    },
    {
      name: "Can create auctions",
      selector: (data) => data.auctionStatus,
      cell: (data) => <RightsChip auctionStatus={data.auctionStatus} />,
      sortable: true,
      width: "170px",
    },
    {
      name: "Action",
      width: "100px",
      sortable: false,
      cell: (data) => (
        <Button variant="contained" onClick={(event) => openMenu(event, data)}>
          <MoreVertIcon />
        </Button>
      ),
    },
  ];

  // FIX 3: Reset editProfile when modal opens/closes
  const [modalOpen, setModalOpen] = useState({});
  const [editProfile, setEditProfile] = useState(false);

  const handleOpen = (text) => {
    // Clear any existing form data first
    setUserInfoSeller({
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      alternateContactNumber: "",
      bi_tin_ein: "",
      businessType: "",
      companyName: "",
      city: "",
      country: "",
      postalCode: "",
      state: "",
      streetOne: "",
      streetTwo: "",
      title: "",
    });

    setEditProfile(false); // Reset edit mode when opening modal
    setErrors({}); // Clear any validation errors

    setModalOpen({
      visible: true,
      type: text,
    });

    // Refresh profile data when opening modal to ensure latest values
    if (sellerId) {
      getProfileData(sellerId);
    }
  };

  const handleCloseModal = (text) => {
    setEditProfile(false); // Reset edit mode when closing modal
    setModalOpen({
      visible: false,
      type: text,
    });
    // Clear form data when closing modal
    setUserInfoSeller({
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      alternateContactNumber: "",
      bi_tin_ein: "",
      businessType: "",
      companyName: "",
      city: "",
      country: "",
      postalCode: "",
      state: "",
      streetOne: "",
      streetTwo: "",
      title: "",
    });
    setErrors({});
  };

  const userData = getUserData() ?? {};

  const [profileDetail, setProfileDetail] = useState({
    apiState: "loading",
    list: "",
  });

  const getProfileData = async (sellerId) => {
    try {
      const { data } = await getSellerDetails(sellerId);
      if (data) {
        setProfileDetail({ apiState: "success", list: data });
        // FIX 2: Load states when profile data is loaded
        if (data.country) {
          stateData(data.country);
        }
      } else {
        throw new Error(data);
      }
    } catch (error) {
      console.clear();
      console.log(error);
    }
  };

  const [passwordType, setPasswordType] = useState("password");
  const [passwordInput, setPasswordInput] = useState("");

  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      return;
    }
    setPasswordType("password");
  };

  // Validation Form
  const [errors, setErrors] = useState({});

  const validate = (event, name, value) => {
    const re = /^[A-Za-z ]+$/;

    switch (name) {
      case "firstName":
        if (!re.test(value)) {
          setErrors({
            ...errors,
            firstName: "First Name should contains only Letters",
          });
        } else {
          let newObj = omit(errors, "firstName");
          setErrors(newObj);
        }
        break;

      case "lastName":
        if (!re.test(value)) {
          setErrors({
            ...errors,
            lastName: "Last Name should contains only Letters",
          });
        } else {
          let newObj = omit(errors, "lastName");
          setErrors(newObj);
        }
        break;

      case "email":
        if (
          !new RegExp(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          ).test(value)
        ) {
          setErrors({
            ...errors,
            email: "Enter a valid email address",
          });
        } else {
          let newObj = omit(errors, "email");
          setErrors(newObj);
        }
        break;

      default:
        break;
    }
  };

  const [userInfoSeller, setUserInfoSeller] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    alternateContactNumber: "",
    bi_tin_ein: "",
    businessType: "",
    companyName: "",
    city: "",
    country: "",
    postalCode: "",
    state: "",
    streetOne: "",
    streetTwo: "",
    title: "",
  });

  const handleChangeSeller = (event) => {
    event.persist();
    validate(event, event.target.name, event.target.value);
    if (event.target.name === "country") stateData(event.target.value);
    setUserInfoSeller({
      ...userInfoSeller,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmitSeller = async (event) => {
    try {
      event.preventDefault();

      if (!isEmpty(errors)) {
        toast("Please fill correct value on all fields. !");
      } else {
        // Create payload with fallback values
        const payload = {
          firstName: userInfoSeller.firstName || profileDetail.list.firstName,
          lastName: userInfoSeller.lastName || profileDetail.list.lastName,
          alternateContactNumber:
            userInfoSeller.alternateContactNumber ||
            profileDetail.list.alternateContactNumber,
          contactNumber:
            userInfoSeller.contactNumber || profileDetail.list.contactNumber,
          email: userInfoSeller.email || profileDetail.list.email,
          companyName:
            userInfoSeller.companyName || profileDetail.list.companyName,
          bi_tin_ein:
            userInfoSeller.bi_tin_ein || profileDetail.list.bi_tin_ein,
          businessType:
            userInfoSeller.businessType || profileDetail.list.businessType,
          city: userInfoSeller.city || profileDetail.list.city,
          country: userInfoSeller.country || profileDetail.list.country,
          postalCode:
            userInfoSeller.postalCode || profileDetail.list.postalCode,
          state: userInfoSeller.state || profileDetail.list.state,
          streetOne: userInfoSeller.streetOne || profileDetail.list.streetOne,
          streetTwo: userInfoSeller.streetTwo || profileDetail.list.streetTwo,
          title: userInfoSeller.title || profileDetail.list.title,
        };

        const { data } = await updateSellerAPI(payload, sellerId);

        if (data) {
          // saved, but the mobile number is also used by another account
          if (data.warning) toast.warning(data.warning);
          else toast("Profile Updated Successfully. !");
          sellerList();
          setModalOpen({
            visible: false,
          });
          setUserInfoSeller({
            firstName: "",
            lastName: "",
            email: "",
            contactNumber: "",
            alternateContactNumber: "",
            bi_tin_ein: "",
            businessType: "",
            companyName: "",
            city: "",
            country: "",
            postalCode: "",
            state: "",
            streetOne: "",
            streetTwo: "",
            title: "",
          });
        }
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response) {
        // the server answered (e.g. "Email already exists"): say it once
        toast(error.response.data?.error || "An error occurred");
      } else if (error.request) {
        // no answer at all
        toast("Network error!");
      } else {
        console.log("Error", error.message);
      }
    }
  };

  // exports what is on screen: the filters and search apply
  const exportToXLSX = (sellers) => {
    const rows = sellers.map((seller) =>
      EXPORT_FIELDS.map(([, value]) => value(seller))
    );
    const worksheet = XLSX.utils.aoa_to_sheet([
      EXPORT_FIELDS.map(([header]) => header),
      ...rows,
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Organisers");
    XLSX.writeFile(workbook, `organisers-${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  const renderComponent = () => {
    switch (changeView) {
      case "AuctionView":
        return (
          <AuctionView
            Bidlog={() => {
              SetChangeView("AuctionBid");
            }}
            BidlogSecond={(eventItemId) => {
              SetChangeView("BidlogSecond");
              SetEvenItemtId(eventItemId);
            }}
            AuctionTable={() => {
              SetChangeView("AuctionTable");
            }}
            eventId={eventId}
          />
        );
      case "AuctionBid":
        return (
          <BidlogFirst
            DashBuyerDetail={(buyerId) => {
              SetChangeView("DashBuyerDetail");
              SetBuyerId(buyerId);
            }}
            AuctionView={() => {
              SetChangeView("AuctionView");
            }}
            eventId={eventId}
            AuctionViewFirst={() => {
              SetChangeView("AuctionViewFirst");
            }}
            AuctionSummary={() => {
              SetChangeView("AuctionSummary");
            }}
          />
        );
      case "BidlogSecond":
        return (
          <BidlogSecond
            AuctionView={() => {
              SetChangeView("AuctionView");
            }}
            eventItemId={eventItemId}
            eventId={eventId}
          />
        );
      case "DashBuyerDetail":
        return (
          <DashBuyerDetail
            DashEventDetail={(buyerId, buyerEventId) => {
              SetChangeView("DashEventDetail");
              SetBuyerEventId(buyerEventId);
            }}
            BidlogFirst={() => {
              SetChangeView("AuctionBid");
            }}
            buyerId={buyerId}
          />
        );

      case "DashEventDetail":
        return (
          <DashEventDetail
            BuyerBidLog={(eventItemId) => {
              SetChangeView("BuyerBidLog");
              SetBuyerEvenItemtId(eventItemId);
            }}
            DashBuyerDetail={(buyerId) => {
              SetChangeView("DashBuyerDetail");
              SetBuyerId(buyerId);
            }}
            buyerId={buyerId}
            buyerEventId={buyerEventId}
          />
        );
      case "BuyerBidLog":
        return (
          <BuyerBidLog
            DashEventDetail={() => {
              SetChangeView("DashEventDetail");
            }}
            buyerId={buyerId}
            buyerEventId={buyerEventId}
            buyerEventItemId={buyerEventItemId}
          />
        );
      case "EditTabEvent":
        return <EditTabEvent />;

      case "EditEvent":
        return (
          <EditEvent
            AuctionTable={() => {
              SetChangeView("AuctionTable");
            }}
            EditTabEvent={() => {
              SetChangeView("EditTabEvent");
            }}
          />
        );
      case "AuctionTable":
        return (
          <AuctionTable
            onView={(eventId) => {
              SetChangeView("AuctionView");
              SetEventId(eventId);
            }}
            onEdit={() => {
              SetChangeView("EditEvent");
            }}
            buyerId={buyerId}
          />
        );
      case "AuctionViewFirst":
        return (
          <AuctionViewFirst
            BidlogFirst={() => {
              SetChangeView("AuctionBid");
            }}
          />
        );
      case "AuctionSummary":
        return (
          <AuctionSummary
            BidlogFirst={() => {
              SetChangeView("AuctionBid");
            }}
          />
        );
      default:
        return (
          <div className="AuctionTable">
            {userData.role !== "SELLER" && userData.role !== "BUYER" ? (
              <>
                <h3>Organiser List</h3>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: "#F3F4F6",
                    color: "#374151",
                  }}
                >
                  {AUTO_RULE_TEXT}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <TextField
                    size="small"
                    label="Search name, company, email, mobile"
                    sx={{ minWidth: 280 }}
                    value={filters.search}
                    onChange={(event) =>
                      setFilters({ ...filters, search: event.target.value })
                    }
                  />
                  <TextField
                    select
                    size="small"
                    label="Status"
                    sx={{ minWidth: 140 }}
                    value={filters.status}
                    onChange={(event) =>
                      setFilters({ ...filters, status: event.target.value })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Can create auctions"
                    sx={{ minWidth: 180 }}
                    value={filters.rights}
                    onChange={(event) =>
                      setFilters({ ...filters, rights: event.target.value })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                  </TextField>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    {filteredSellers.length} of {tableSellers.length} organisers
                  </Typography>
                  <Tooltip title="Export the organisers shown to Excel">
                    <Button onClick={() => exportToXLSX(filteredSellers)}>
                      <FaRegFileExcel
                        style={{ fontSize: "30px", color: "#04aa18", paddingTop: "4px" }}
                      />
                    </Button>
                  </Tooltip>
                </Box>

                <DataTable
                  columns={columns}
                  data={filteredSellers}
                  keyField="userId"
                  pagination
                  highlightOnHover
                  className="DataTable"
                  progressPending={!listLoaded}
                  noDataComponent="No organisers match these filters"
                />
              </>
            ) : (
              <Navigate to={{ pathname: "/dashboard" }} />
            )}

            {/* the ⋮ menu of the row that was clicked */}
            <Popover
              open={Boolean(menu)}
              anchorEl={menu?.anchorEl}
              onClose={closeMenu}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              {menu && (
                <Typography component="div" sx={{ p: 2, display: "grid" }}>
                  <div
                    className={classes.viewBtn}
                    onClick={() => {
                      closeMenu();
                      SetChangeView("AuctionTable");
                    }}
                  >
                    <RemoveRedEyeIcon
                      style={{ color: "#2D55EB", paddingRight: "5px" }}
                    />
                    Report
                  </div>
                  <span
                    className={classes.viewBtn}
                    onClick={() => {
                      closeMenu();
                      handleOpen();
                    }}
                  >
                    <EditIcon style={{ color: "#2D55EB", paddingRight: "5px" }} />
                    Edit
                  </span>
                  <span
                    className={classes.viewBtn}
                    onClick={() => handleAccessSellerAccount(menu.seller)}
                  >
                    <LoginIcon style={{ color: "#2D55EB", paddingRight: "5px" }} />
                    Access Account
                  </span>
                  {menu.seller.status === "ACTIVE" ? (
                    <span
                      className={classes.viewBtn}
                      onClick={() => askToConfirm("DEACTIVATE", menu.seller)}
                    >
                      <CancelIcon style={{ color: "red", paddingRight: "5px" }} />
                      Deactivate
                    </span>
                  ) : (
                    <span
                      className={classes.viewBtn}
                      onClick={() => askToConfirm("ACTIVATE", menu.seller)}
                    >
                      <CheckIcon style={{ color: "green", paddingRight: "5px" }} />
                      Activate
                    </span>
                  )}
                  {menu.seller.auctionStatus === "NO" ? (
                    <span
                      className={classes.viewBtn}
                      onClick={() => askToConfirm("GRANT_RIGHTS", menu.seller)}
                    >
                      <CheckIcon style={{ color: "green", paddingRight: "5px" }} />
                      Allow auction creation
                    </span>
                  ) : (
                    <span
                      className={classes.viewBtn}
                      onClick={() => askToConfirm("WITHDRAW_RIGHTS", menu.seller)}
                    >
                      <CancelIcon style={{ color: "red", paddingRight: "5px" }} />
                      Stop auction creation
                    </span>
                  )}
                  <span
                    className={classes.viewBtn}
                    onClick={() => askToConfirm("DELETE", menu.seller)}
                  >
                    <DeleteIcon style={{ color: "red", paddingRight: "5px" }} />
                    Delete
                  </span>
                </Typography>
              )}
            </Popover>

            {/* one confirm dialog for every action */}
            <Dialog
              open={Boolean(pending)}
              onClose={() => !isLoading && setPending(null)}
              aria-labelledby="organiser-action-title"
              aria-describedby="organiser-action-description"
            >
              {pending && (
                <>
                  <DialogTitle id="organiser-action-title">
                    {ACTIONS[pending.action].title(pending.seller.userName)}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="organiser-action-description">
                      {ACTIONS[pending.action].body}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setPending(null)} disabled={isLoading}>
                      Cancel
                    </Button>
                    <Button
                      onClick={runPendingAction}
                      disabled={isLoading}
                      color={
                        ["DELETE", "DEACTIVATE", "WITHDRAW_RIGHTS"].includes(
                          pending.action
                        )
                          ? "error"
                          : "primary"
                      }
                      autoFocus
                    >
                      {ACTIONS[pending.action].confirm}
                    </Button>
                  </DialogActions>
                </>
              )}
            </Dialog>

            <SellerProfileModal
              open={modalOpen.visible}
              handleClose={handleCloseModal}
              profileDetail={profileDetail.list}
              handleChangeSeller={handleChangeSeller}
              handleSubmitSeller={handleSubmitSeller}
              userInfoSeller={userInfoSeller}
              errors={errors}
              editProfile={editProfile}
              setEditProfile={setEditProfile}
              setErrors={setErrors}
              passwordType={passwordType}
              togglePassword={togglePassword}
              setPasswordInput={setPasswordInput}
              passwordInput={passwordInput}
              countriesList={countriesList.list}
              stateList={stateList.list}
              companyName={
                userInfoSeller.companyName || profileDetail.list.companyName
              }
              businessType={
                userInfoSeller.businessType || profileDetail.list.businessType
              }
              bi_tin_ein={
                userInfoSeller.bi_tin_ein || profileDetail.list.bi_tin_ein
              }
              streetOne={
                userInfoSeller.streetOne || profileDetail.list.streetOne
              }
              streetTwo={
                userInfoSeller.streetTwo || profileDetail.list.streetTwo
              }
              city={userInfoSeller.city || profileDetail.list.city}
              country={userInfoSeller.country || profileDetail.list.country}
              postalCode={
                userInfoSeller.postalCode || profileDetail.list.postalCode
              }
              state={userInfoSeller.state || profileDetail.list.state}
              firstName={
                userInfoSeller.firstName || profileDetail.list.firstName
              }
              lastName={userInfoSeller.lastName || profileDetail.list.lastName}
              email={userInfoSeller.email || profileDetail.list.email}
              title={userInfoSeller.title || profileDetail.list.title}
              contactNumber={
                userInfoSeller.contactNumber || profileDetail.list.contactNumber
              }
              alternateContactNumber={
                userInfoSeller.alternateContactNumber ||
                profileDetail.list.alternateContactNumber
              }
            />
          </div>
        );
    }
  };
  return (
    <div className={classes.section}>
      <div className="container">
        <div className={classes.Dashboard}>
          <div className={classes.dashboardCard}>
            <DashboardMenu />
            <div className={classes.dashboardCards}>{renderComponent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashSeller;
